// Shown INSTEAD of the game when labs is running inside a native app's WebView.
//
// WHY. The nerdle app claims nerdlegame.com/* (iOS entitlements `applinks:nerdlegame.com`,
// Android manifest `android:host="nerdlegame.com"`), so recruitment links open in its WebView.
// That WebView cannot record: no RECORD_AUDIO on Android, no NSMicrophoneUsageDescription on
// iOS. Android reaches our mic-error screen; iOS is killed by the OS the instant getUserMedia
// is called. Measured cost: 246 consented sessions, 48 people, ZERO traces, from 2026-07-13.
//
// WHY IT MUST RENDER BEFORE ANYTHING ELSE. Once a device has the consent latch
// (`labs_consented_v1`), each game's mount effect calls handleStart() → requestMic() with no
// user interaction at all. So a returning in-app participant crashes before any screen paints —
// which is exactly what RM saw: first visit crashed on tapping Continue, every later visit
// crashed instantly. This component is therefore gated ahead of that effect, not inside it.
//
// WHY COPY-AND-PASTE RATHER THAN A REDIRECT. We tested the elegant version: eject to a domain
// the app does not claim, then bounce back to nerdlegame.com. The eject works and the payload
// survives it, but the bounce back is a universal link and iOS recaptured it into the app on
// the first attempt, then behaved differently on the second — the difference being a per-domain
// preference iOS sets silently when a user returns to Safari. Unreliable, and invisible to us.
// A PASTED URL never triggers a universal link, so this is the one route that is deterministic
// on a device we have never seen. It costs a paste; the alternative costs the whole session.
//
// KNOWN RESIDUAL WEAKNESS: Safari shows its own non-dismissable "Nerdle · Open" bar on the
// pasted page, because the URL is still on a claimed domain. Tapping it returns the participant
// to the app, where the consent latch crashes them instantly. There is no way to suppress that
// from the page — only excluding this path in apple-app-site-association would remove it, and
// that must be a path the recruitment link does NOT use, or the email would open in the browser
// and the whole point (reading the app's localStorage) is lost. Hence the wording below leads
// with the REASON rather than a bare prohibition: "don't tap that" invites tapping.

import { useEffect, useState } from 'react'
import { Brand, LabsGame } from '../components/Brand'
import { getNerdleSkill, encodeHandoff } from './nerdleSkill'
import { track } from './analytics'

/**
 * The URL the participant pastes. Built HERE, inside the app, because that is the only place
 * the classic stats are readable — copying the emailed link would carry nothing. Same path and
 * token they arrived with, plus the covariates in the fragment.
 */
export const buildHandoffUrl = (): string => {
  const { origin, pathname, search } = window.location
  return origin + pathname + search + '#h=' + encodeHandoff(getNerdleSkill())
}

export const BrowserHandoff = ({ game }: { game: LabsGame }) => {
  const [copied, setCopied] = useState(false)
  const url = buildHandoffUrl()

  // COUNT THE DIVERSION. Until now this screen was silent, so everyone it caught was invisible:
  // `labs_consent_view` fires from the game's mount effect regardless of what renders, so an
  // in-app participant counted as a LANDING and then could never fire `labs_consent`. That made
  // the in-app share indistinguishable from a fall in consent rate - which cost us days of
  // looking in the wrong place. labs_handoff_view is the denominator that separates them.
  useEffect(() => {
    track('labs_handoff_view', { game })
  }, [game])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      track('labs_handoff_copy', { game, method: 'clipboard' })
    } catch {
      track('labs_handoff_copy', { game, method: 'select' })
      // Older WebViews have no async clipboard. Select the text so one long-press → Copy works.
      const el = document.getElementById('labs-handoff-url') as HTMLInputElement | null
      if (el) {
        el.focus()
        el.setSelectionRange(0, el.value.length)
      }
      setCopied(false)
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <Brand game={game} />

      <h1 className="mt-6 text-xl font-semibold">Open this in your browser</h1>

      <p className="mt-3 text-sm leading-relaxed">
        This game needs your microphone, and the nerdle app can&rsquo;t share it. It takes two
        taps to move across &mdash; your invitation still works.
      </p>

      <ol className="mt-5 space-y-4 text-sm leading-relaxed">
        <li>
          <b>1. Copy the link.</b>
          <button
            type="button"
            onClick={copy}
            className="mt-2 w-full rounded-lg bg-[#820458] py-3 font-semibold text-white"
          >
            {copied ? 'Copied ✓' : 'Copy link'}
          </button>
          <input
            id="labs-handoff-url"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-600"
          />
        </li>
        <li>
          <b>2. Open Safari or Chrome</b> and paste it into the address bar.
        </li>
        <li>
          <b>3. Stay in your browser.</b> A &ldquo;Nerdle &mdash; Open&rdquo; bar may appear at
          the top of the page. Ignore it &mdash; it brings you back here, where the microphone
          doesn&rsquo;t work.
        </li>
      </ol>

      <p className="mt-6 text-xs leading-relaxed text-gray-500">
        Sorry for the detour. We record your voice only while you&rsquo;re solving, and the link
        above carries nothing that identifies you.
      </p>
    </div>
  )
}
